/* eslint-disable indent */
import 'rc-slider/assets/index.css'
import { useRouter } from 'next/router'
import { useState, useEffect, Fragment } from 'react'
import Slider from 'rc-slider'
import { sortBy, isNumber, chain } from 'lodash'

import { Layout, HomePost, Pagination } from '../../components/front'
import { getProductCategories, getUniqueProducts, getProductColors, getStaticDate } from '../../lib'
import { useCart } from '../../components/front/Cart'
import { withSession } from '../../utils/server'

function colorDistance(color1, color2) {
    // This is actually the square of the distance but
    // this doesn't matter for sorting.
    let result = 0
    for (let i = 0; i < color1.length; i++) result += (color1[i] - color2[i]) * (color1[i] - color2[i])
    return result
}
function sortColors(colors) {
    if (colors.length <= 1) return colors
    // Calculate distance between each color
    let distances = []
    for (let i = 0; i < colors.length; i++) {
        distances[i] = []
        for (let j = 0; j < i; j++) distances.push([colors[i], colors[j], colorDistance(colors[i], colors[j])])
    }
    distances.sort(function (a, b) {
        return a[2] - b[2]
    })

    // Put each color into separate cluster initially
    let colorToCluster = {}
    for (let i = 0; i < colors.length; i++) colorToCluster[colors[i]] = [colors[i]]

    // Merge clusters, starting with lowest distances
    let lastCluster
    for (let i = 0; i < distances.length; i++) {
        let color1 = distances[i][0]
        let color2 = distances[i][1]
        let cluster1 = colorToCluster[color1]
        let cluster2 = colorToCluster[color2]
        if (!cluster1 || !cluster2 || cluster1 == cluster2) continue

        // Make sure color1 is at the end of its cluster and
        // color2 at the beginning.
        if (color1 != cluster1[cluster1.length - 1]) cluster1.reverse()
        if (color2 != cluster2[0]) cluster2.reverse()

        // Merge cluster2 into cluster1
        cluster1.push.apply(cluster1, cluster2)
        delete colorToCluster[color1]
        delete colorToCluster[color2]
        colorToCluster[cluster1[0]] = cluster1
        colorToCluster[cluster1[cluster1.length - 1]] = cluster1
        lastCluster = cluster1
    }

    // By now all colors should be in one cluster
    return lastCluster
}
function hexToRgb(hex) {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null
}
function rgbToHex(rgb) {
    return '#' + ((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2]).toString(16).slice(1)
}

export let getServerSideProps = withSession(async ({ query }) => {
    const hier = await getProductCategories()
    const colorsCollection = await getProductColors()
    const data = await getStaticDate()

    const colors = {
        ids: sortColors(colorsCollection.ids.map((id) => hexToRgb(colorsCollection.byIds[id].value))).map((c) =>
            rgbToHex(c)
        ),
        byIds: chain(colorsCollection.ids.map((id) => colorsCollection.byIds[id]))
            .keyBy('value')
            .mapValues((o) => o)
            .value()
    }

    let filter = {}

    //get selected categories from url path
    if (query && query.path) {
        const categoryPath = Array.isArray(query.path)
            ? query.path?.map((q) => decodeURIComponent(q))
            : [decodeURIComponent(query.path)]
        if (categoryPath && categoryPath.length >= 0) {
            filter.category_name = [categoryPath[0]]
            const toplevelCat = hier.find((h) => h.title === categoryPath[0])
            if (toplevelCat && toplevelCat.children)
                filter.category_name = [...filter.category_name, ...toplevelCat.children.map((c) => c.title)]
        }
    }

    const [entities, total] = await getUniqueProducts({ filter: filter }, true)

    return {
        props: {
            productCategories: hier,
            products: entities,
            colors: colors,
            filter: filter,
            total: total,
            footer: data['about_footer'].content
        }
    }
})

export default function ProductsList({ productCategories, products, colors, filter, total, footer }) {
    const router = useRouter()
    const [totalRecords, setTotalRecords] = useState(() => total)
    const { createSliderWithTooltip } = Slider
    const Range = createSliderWithTooltip(Slider.Range)
    const sliderItem = { min: 0, max: 1000000 }
    const [query, setQuery] = useState('')
    const [sortOrder, setSortOrder] = useState(() => 'created_at desc')
    const [page, setPage] = useState(() => 0)
    const [priceFilter, setPriceFilter] = useState([sliderItem.min, sliderItem.max])
    const [productfilter, setProductfilter] = useState(filter)
    const [filteredProducts, setFilteredProducts] = useState(products)
    const cart = useCart()

    useEffect(() => {
        setProductfilter(filter)
        setFilteredProducts(products)
    }, [filter, products])

    useEffect(() => {
        if (router.query.search) {
            setQuery(router.query.search)
            changeFilter({ ...productfilter, name: router.query.search })
        }
    }, [router.query.search])

    function varToQueryParam(v) {
        if (v === null) return 'null'
        if (isNumber(v)) return v + ''
        if (Array.isArray(v))
            return `[${v.map((i) => {
                if (isNumber(i)) return i + ''
                if (i === null) return 'null'
                return `"${i}"`
            })}]`
        return `"${v}"`
    }
    async function changeFilter(newfilter, newOrder, page = 0) {
        setProductfilter(newfilter)
        setPage(page)
        let queryfilter = { type: 'product', parent_id: null }
        if (newfilter.category_name) queryfilter.category_name = newfilter.category_name
        if (newfilter.color) queryfilter.color = newfilter.color
        if (newfilter.price_min) queryfilter.price_min = newfilter.price_min
        if (newfilter.price_max) queryfilter.price_max = newfilter.price_max
        if (newfilter.name) queryfilter.name = newfilter.name

        const response = await fetch('/api/graphql', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                query: `
                query {
                    ents(query: {${Object.keys(queryfilter).map(
                        (k) => `${k}: ${varToQueryParam(queryfilter[k])}`
                    )}}, meta: { page:${page}, pagesize: 12, order: ["availability", "${newOrder || sortOrder}"] }) {
                        success
                        message
                        meta {
                            page
                            pagesize
                            order
                            count
                        }
                        entities {
                            id
                            name
                            abst
                            des
                            metas{
                                id
                                key
                                value
                                type
                            }
                            metas_dic
                            content
                            category_id
                            category_name
                            parent_id
                            parent_name
                            children_count
                            stock
                            created_at
                            createdby_id
                            modified_at
                            modifiedby_id
                        }
                    }
                }`
            })
        })
        const responseObj = await response.json()
        setTotalRecords(responseObj.data.ents.meta.count)
        for (let ent of responseObj.data.ents.entities) {
            ent.metas_cus = ent.metas.filter((m) => m.type === 'attr_cus')
            for (let meta of ent.metas_cus) {
                meta.created_at += ''
                meta.modified_at += ''
            }
            ent.metas = ent.metas.filter((m) => m.type !== 'attr_cus')
            let dic = {}
            for (let meta of ent.metas) {
                meta.created_at += ''
                meta.modified_at += ''

                dic[meta.key] = meta
            }
            ent.metas = dic
            ent.created_at += ''
            ent.modified_at += ''
        }

        setFilteredProducts(responseObj.data.ents.entities)
    }

    const CatListItem = function ({ category }) {
        return (
            <label className="flex items-center mb-2 p-1.5">
                <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={
                        productfilter.category_name &&
                        ((productfilter.category_name.length === 1 &&
                            productfilter.category_name[0] === category.title) ||
                            (productfilter.category_name.length > 1 &&
                                !category.parent_id &&
                                productfilter.category_name.indexOf(category.title) !== -1))
                    }
                    onChange={() => {
                        if (productfilter.category_name && productfilter.category_name[0] === category.title)
                            changeFilter({ ...productfilter, category_name: null })
                        else {
                            if (category.children && !category.parent_id && category.children.length !== 0)
                                changeFilter({
                                    ...productfilter,
                                    category_name: [category.title, ...category.children.map((c) => c.title)]
                                })
                            else changeFilter({ ...productfilter, category_name: [category.title] })
                        }
                    }}
                />
                <span className="mr-2 text-gray-600">{category.title || category.name}</span>
            </label>
        )
    }

    const catList = sortBy(productCategories, 'title').map((category) => (
        <Fragment key={category.id}>
            <CatListItem category={category} />
            <div className="ms-5">
                {sortBy(category.children, 'title').map((child) => (
                    <CatListItem category={child} key={child.id} />
                ))}
            </div>
        </Fragment>
    ))

    const colorList = colors.ids.map((color) => {
        return (
            <li key={colors.byIds[color].id} className="mb-2">
                <label className="flex flex-wrap items-center p-1.5">
                    <input
                        type="checkbox"
                        className="form-checkbox flex-none"
                        checked={productfilter.color === colors.byIds[color].value}
                        onChange={() => {
                            if (productfilter.color && productfilter.color === colors.byIds[color].value)
                                changeFilter({ ...productfilter, color: null })
                            else changeFilter({ ...productfilter, color: colors.byIds[color].value })
                        }}
                    />
                    <span className="mr-2 text-gray-600 w-24 flex-1">{colors.byIds[color].key}</span>
                    <span
                        style={{ backgroundColor: colors.byIds[color].value }}
                        className="rounded-xl w-4 h-4 border flex-none"
                    ></span>
                </label>
            </li>
        )
    })
    const colorFilterMobile = (
        <select className="flex-col appearance-none text-gray-600 w-full bg-white border border-gray-400 shadow-inner px-4 py-2 pr-8 rounded focus:outline-none focus:border-pink-300">
            <option>رنگ</option>
            {colors.ids.map((color, idx) => (
                <option key={idx}>{colors.byIds[color].key}</option>
            ))}
        </select>
    )

    const categoryFilterMobile = (
        <select
            className="flex-col appearance-none text-gray-600 w-full bg-white border border-gray-400 shadow-inner px-4 py-2 pr-8 rounded focus:outline-none focus:border-pink-300"
            selected={productfilter.category_name}
        >
            <option>دسته بندی</option>
            {productCategories
                .filter((p) => p.parent_id === null)
                .map((category) => (
                    <option key={category.id}>{category.title}</option>
                ))}
        </select>
    )

    const produstPosts = filteredProducts.map((product) => {
        return (
            <HomePost
                enName={product?.metas?.alt?.value}
                faName={product?.name}
                href={`/products/detail/${product.id}/${encodeURIComponent(product.name)}`}
                imageUrl={product?.metas?.pic_path?.value}
                inCart={cart.exists(product.id)}
                key={product.id}
                product={product}
                stock={product?.stock}
            />
        )
    })
    function log(value) {
        setPriceFilter(value) //eslint-disable-line
    }

    return (
        <Layout menu={productCategories} footer={{ about: footer }}>
            <div
                className="pageCover w-full lg:block lg:h-80"
                style={{
                    backgroundImage: 'url("/img/banner-stationery2.JPG")',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            ></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 bg-gray-200 pt-10">
                <div className=" hidden lg:block lg:col-span-1 p-4 ">
                    <h2 className="text-lg font-bold text-purple-400 text-right border-b border-gray-400">
                        <span className="mr-4 border-b-2 border-purple-400">فیلترها</span>
                    </h2>
                    <div className="bg-white rounded-md py-6 text-right mt-4">
                        <h3 className="px-6 mb-4 font-bold">دسته بندی</h3>
                        <div className="px-6" style={{ maxHeight: 500, overflow: 'auto' }}>
                            {catList}
                        </div>
                    </div>
                    <div className="bg-white rounded-md p-6 text-right mt-4 pb-10">
                        <h3 className="mb-4 font-bold">جستجو در دسته بندی</h3>
                        <input
                            type="text"
                            className="flex-col appearance-none text-gray-600 w-full bg-white border border-gray-400 shadow-inner px-4 py-2 pr-8 rounded focus:outline-none focus:border-pink-300"
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value)
                            }}
                        />
                        <div className="flex space-between">
                            {productfilter.name && (
                                <button
                                    type="submit"
                                    className={
                                        'rounded bg-pink-500 mt-10 w-5/12  block mx-auto h-10 text-gray-50 hover:bg-pink-300 focus:bg-pink-300 hover:text-white'
                                    }
                                    onClick={() => {
                                        changeFilter({ ...productfilter, name: null })
                                    }}
                                >
                                    ریست
                                </button>
                            )}
                            <button
                                type="submit"
                                className={`rounded bg-purple-500 mt-10 ${
                                    productfilter.name ? 'w-5/12' : 'w-9/12'
                                } bloclk  mx-auto h-10 text-gray-50 hover:bg-pink-300 focus:bg-pink-300 hover:text-white`}
                                onClick={() => {
                                    changeFilter({ ...productfilter, name: query })
                                }}
                            >
                                اعمال
                            </button>
                        </div>
                    </div>
                    <div className="bg-white rounded-md p-6 text-right mt-4 pb-10">
                        <h3 className="mb-4 font-bold">رنج قیمت</h3>
                        <Range
                            min={sliderItem.min}
                            max={sliderItem.max}
                            defaultValue={priceFilter}
                            tipFormatter={(value) => `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} تومان`}
                            allowCross={false}
                            marks={{
                                0: `${sliderItem.min.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} تومان`,
                                1000000: `${sliderItem.max.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} تومان`
                            }}
                            onAfterChange={log}
                        />
                        <div className="flex space-between">
                            {(productfilter.price_min || productfilter.price_max) && (
                                <button
                                    type="submit"
                                    className={
                                        'rounded bg-pink-500 mt-10 w-5/12  block mx-auto h-10 text-gray-50 hover:bg-pink-300 focus:bg-pink-300 hover:text-white'
                                    }
                                    onClick={() => {
                                        changeFilter({
                                            ...productfilter,
                                            price_min: null,
                                            price_max: null
                                        })
                                    }}
                                >
                                    ریست
                                </button>
                            )}
                            <button
                                type="submit"
                                className={`rounded bg-purple-500 mt-10 ${
                                    productfilter.price_min || productfilter.price_max ? 'w-5/12' : 'w-9/12'
                                } bloclk  mx-auto h-10 text-gray-50 hover:bg-pink-300 focus:bg-pink-300 hover:text-white`}
                                onClick={() => {
                                    changeFilter({
                                        ...productfilter,
                                        price_min: priceFilter[0],
                                        price_max: priceFilter[1]
                                    })
                                }}
                            >
                                اعمال
                            </button>
                        </div>
                    </div>
                    <div className="bg-white rounded-md p-6 text-right mt-4">
                        <h3 className="mb-4 font-bold">رنگ</h3>
                        <ul>{colorList}</ul>
                    </div>
                </div>
                <div className="lg:col-span-3">
                    <div className="flex gap-1 lg:hidden rounded bg-gray-300 p-2 m-4">
                        {colorFilterMobile}

                        {categoryFilterMobile}
                    </div>
                    <div className="flex gap-1 rounded-md bg-gray-300 p-2 m-4">
                        <div className="w-full lg:hidden flex">
                            <input
                                placeholder="نام"
                                type="text"
                                className="flex-1 flex-col appearance-none text-gray-600 w-full bg-white border border-gray-400 shadow-inner px-4 py-2 pr-8 rounded focus:outline-none focus:border-pink-300"
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value)
                                }}
                            />
                            <div className="flex-none flex space-between w-36">
                                <button
                                    type="submit"
                                    className={`rounded bg-purple-500 ${
                                        productfilter.name ? 'w-5/12' : 'w-9/12'
                                    } bloclk  mx-auto h-10 text-gray-50 hover:bg-pink-300 focus:bg-pink-300 hover:text-white`}
                                    onClick={() => {
                                        changeFilter({ ...productfilter, name: query })
                                    }}
                                >
                                    اعمال
                                </button>
                                {productfilter.name && (
                                    <button
                                        type="submit"
                                        className={
                                            'rounded bg-pink-500 w-5/12  block mx-auto h-10 text-gray-50 hover:bg-pink-300 focus:bg-pink-300 hover:text-white'
                                        }
                                        onClick={() => {
                                            setQuery('')
                                            changeFilter({ ...productfilter, name: null })
                                        }}
                                    >
                                        ریست
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="w-60 mx-auto lg:mx-0">
                            <div className="flex-shrink w-full inline-block relative">
                                <select
                                    className="flex-col appearance-none text-gray-600 w-full bg-white border border-gray-400 shadow-inner px-4 py-2 pr-8 rounded focus:outline-none focus:border-pink-300"
                                    onChange={(e) => {
                                        setSortOrder(e.target.value)
                                        changeFilter(productfilter, e.target.value)
                                    }}
                                    value={sortOrder}
                                >
                                    <option disabled>مرتب سازی بر اساس ...</option>
                                    <option value="sale desc">پرفروش ترین</option>
                                    <option value="visit desc">پربازدید ترین</option>
                                    <option value="created_at desc">جدیدترین</option>
                                    <option value="price asc">ارزان ترین</option>
                                    <option value="price desc">گران ترین</option>
                                </select>

                                <div className="pointer-events-none absolute top-0 mt-3  right-0 flex items-center px-2 text-gray-600">
                                    <svg
                                        className="fill-current h-4 w-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                    >
                                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
                        {produstPosts.length !== 0 && produstPosts}
                        {produstPosts.length === 0 && <p>محصولی یافت نشد</p>}
                    </div>
                    <Pagination
                        current={page + 1}
                        pagesize={12}
                        total={totalRecords}
                        onPageChange={({ current }) => {
                            changeFilter({ ...productfilter }, sortOrder, current - 1)
                        }}
                    />
                </div>
            </div>
        </Layout>
    )
}
